import React from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  evaluateRegistrationEligibility,
  type RegistrationEligibility,
} from '@utils/parentalConsent';

export type ParentalConsentState = {
  consentData: boolean;
  consentTerms: boolean;
  parentalAttestation: boolean;
  consentMarketing: boolean;
};

export const emptyParentalConsent = (): ParentalConsentState => ({
  consentData: false,
  consentTerms: false,
  parentalAttestation: false,
  consentMarketing: false,
});

export function parentalConsentComplete(state: ParentalConsentState): boolean {
  return state.consentData && state.consentTerms && state.parentalAttestation;
}

interface ParentalConsentFieldsProps {
  dateOfBirth?: string;
  country?: string | null;
  childName?: string;
  value: ParentalConsentState;
  onChange: (next: ParentalConsentState) => void;
}

const ParentalConsentFields: React.FC<ParentalConsentFieldsProps> = ({
  dateOfBirth,
  country,
  childName,
  value,
  onChange,
}) => {
  const eligibility: RegistrationEligibility | null = dateOfBirth
    ? evaluateRegistrationEligibility(dateOfBirth, country)
    : null;
  const named = childName?.trim() ? childName.trim() : 'this child';

  return (
    <Box sx={{ mt: 2 }}>
      {eligibility ? (
        <Alert severity={eligibility.requiresParentRegistration ? 'warning' : 'info'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {eligibility.requiresParentRegistration
              ? 'Digital consent required (COPPA · GDPR-K · DPDP)'
              : 'Parent / guardian adding a player'}
          </Typography>
          {eligibility.requiresParentRegistration
            ? `${eligibility.reason || ''} Age: ${eligibility.age}. Confirm the boxes below. That records your parental consent. They can then sign in with their Player ID and the family password.`
            : `This player is at or above the digital age of consent in ${eligibility.country} (${eligibility.consentAge}+). Confirm you are the parent or legal guardian adding them.`}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter the child&apos;s date of birth to see the digital consent requirements for your country.
        </Alert>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={value.consentData}
            onChange={(e) => onChange({ ...value, consentData: e.target.checked })}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            <strong>Required:</strong> I am the parent or legal guardian of {named} and I consent to the
            collection and processing of their personal data (name, date of birth, performance data, contest
            videos) as described in the{' '}
            <Link component={RouterLink} to="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            .
          </Typography>
        }
        sx={{ alignItems: 'flex-start', display: 'flex', width: '100%', mb: 1 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={value.consentTerms}
            onChange={(e) => onChange({ ...value, consentTerms: e.target.checked })}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            <strong>Required:</strong> I accept the{' '}
            <Link component={RouterLink} to="/privacy-policy" target="_blank">
              Terms of Service and Privacy Policy
            </Link>{' '}
            on behalf of {named}.
          </Typography>
        }
        sx={{ alignItems: 'flex-start', display: 'flex', width: '100%', mb: 1 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={value.parentalAttestation}
            onChange={(e) => onChange({ ...value, parentalAttestation: e.target.checked })}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            <strong>Required:</strong> I attest that I am the parent or legal guardian authorized to register this
            player
            {eligibility?.requiresParentRegistration
              ? ', and I understand this attestation is my parental consent for EncycGames.'
              : '.'}
          </Typography>
        }
        sx={{ alignItems: 'flex-start', display: 'flex', width: '100%', mb: 1 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={value.consentMarketing}
            onChange={(e) => onChange({ ...value, consentMarketing: e.target.checked })}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            <em>Optional:</em> I consent to receiving performance updates and contest news at my email.
          </Typography>
        }
        sx={{ alignItems: 'flex-start', display: 'flex', width: '100%' }}
      />
    </Box>
  );
};

export default ParentalConsentFields;
